import {
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  styled,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import LogoutIcon from "@mui/icons-material/Logout";
import TuneIcon from "@mui/icons-material/Tune";
import { COLORS } from "../../constants/colors";
import { useAuthProfile } from "../../contexts";
import { StringUtils } from "../../utils/stringUtils";

const StyledAvatar = styled(Avatar)`
  background-color: ${({ theme }) => theme?.palette?.accent?.main};
  cursor: pointer;
`;

const NameContainer = styled("div")`
  padding: 0.5em 1em;
`;

const UserInfo: FC = () => {
  const queryClient = useQueryClient();
  const profile = useAuthProfile();
  const { signoutRedirect } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const signOut = () => {
    queryClient.clear();
    signoutRedirect();
  };

  const hideMenu = () => {
    setAnchorEl(null);
  };

  const openUdo = () => {
    hideMenu();
    window.open(import.meta.env.VITE_UDO_URL, "_blank");
  };

  const username = `${profile?.first_name || ""} ${profile?.last_name || ""}`;

  const userInfo = useMemo(() => {
    if (profile) {
      return (
        <StyledAvatar>{StringUtils.produceInitials(username)}</StyledAvatar>
      );
    }
    return null;
  }, [profile]);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onKeyPress={(e) => setAnchorEl(e.currentTarget)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {userInfo}
      </div>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: `drop-shadow(0px 2px 8px ${COLORS.DROP_SHADOW})`,
              mb: 1,
              ml: 5.5,
              width: "16.8em",
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: "''",
                display: "block",
                position: "absolute",
                bottom: 9,
                left: -5,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
      >
        <NameContainer>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {username}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginBottom: ".5em",
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          >
            {profile?.management_roles[0]}
          </Typography>
        </NameContainer>
        <Divider sx={{ marginBottom: ".5em" }} />
        <MenuItem onClick={openUdo}>
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Go to UDO</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export { UserInfo };
